import React, { useEffect, useState } from "react";
import { api, formatApiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBranch } from "@/context/BranchContext";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Building2, Store, Upload, LogOut, Camera, Phone, CheckCircle2 } from "lucide-react";
import { PhoneOtpVerifier } from "@/components/PhoneOtpVerifier";

function MyPhoneSection() {
  const { user, refresh } = useAuth();
  const [phone, setPhone] = useState(user?.phone || "+91");
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);

  const onVerified = async (idToken) => {
    setBusy(true);
    try {
      await api.post("/users/me/phone", { firebase_id_token: idToken });
      toast.success("Phone number updated");
      await refresh();
      setEditing(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="brutalist-card p-6 space-y-4" data-testid="my-phone-section">
      <div className="flex items-center gap-2"><Phone className="w-4 h-4" /><h2 className="text-lg font-semibold">Your mobile number</h2></div>
      {!editing ? (
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="label-eyebrow">Current number</div>
            <div className="font-mono text-sm text-zinc-950 mt-1 flex items-center gap-2" data-testid="my-phone-current">
              {user?.phone || <span className="text-zinc-400">Not set</span>}
              {user?.phone_verified && (
                <span className="text-[10px] uppercase tracking-wider text-emerald-700 inline-flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" /> Verified
                </span>
              )}
            </div>
            <p className="text-[11px] font-mono text-zinc-500 mt-2">Used for password-reset OTPs and mobile-OTP sign-in. Changing it requires a fresh SMS verification.</p>
          </div>
          <button
            data-testid="my-phone-change-button"
            onClick={() => { setPhone(user?.phone || "+91"); setEditing(true); }}
            className="px-4 py-2 border border-zinc-300 text-xs uppercase tracking-wider hover:bg-zinc-50 font-semibold whitespace-nowrap"
          >
            Change phone
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-[11px] font-mono text-zinc-500">
            Enter your new number and tap Send OTP. We'll SMS a 6-digit code — enter it to confirm.
          </p>
          <PhoneOtpVerifier
            phone={phone}
            onPhoneChange={setPhone}
            onVerified={onVerified}
            disabled={busy}
            testidPrefix="my-phone"
            autoFocus
          />
          <button
            type="button"
            onClick={() => setEditing(false)}
            data-testid="my-phone-cancel"
            className="text-[11px] uppercase tracking-wider text-zinc-500 hover:text-zinc-900"
          >
            Cancel
          </button>
        </div>
      )}
    </section>
  );
}



function BranchModal({ initial, onClose, onSaved }) {
  const editing = !!initial?.id;
  const [form, setForm] = useState(initial || { name: "", code: "", address: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      if (editing) await api.patch(`/branches/${initial.id}`, form);
      else await api.post("/branches", form);
      toast.success("Branch saved");
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-zinc-950/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border border-zinc-300 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-zinc-200"><h3 className="text-xl font-semibold">{editing ? "Edit" : "New"} branch</h3></div>
        <div className="p-6 space-y-3">
          {[["name","Branch name *"],["code","Short code *"],["address","Address"],["phone","Phone"]].map(([k,l]) => (
            <div key={k}><label className="label-eyebrow block mb-1.5">{l}</label>
              <input data-testid={`branch-${k}`} value={form[k]} onChange={(e) => setForm({...form, [k]: e.target.value})} className="w-full border border-zinc-300 px-3 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
            </div>
          ))}
        </div>
        <div className="px-6 py-4 border-t border-zinc-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 border border-zinc-300 text-sm uppercase tracking-wider hover:bg-zinc-50">Cancel</button>
          <button data-testid="branch-save-button" disabled={busy} onClick={save} className="px-4 py-2 bg-zinc-950 text-white text-sm uppercase tracking-wider hover:bg-zinc-800 disabled:opacity-50">{busy ? "Saving..." : "Save"}</button>
        </div>
      </div>
    </div>
  );
}

export default function Settings() {
  const { user, refresh } = useAuth();
  const { branches, refresh: refreshBranches } = useBranch();
  const [business, setBusiness] = useState(null);
  const [openBranch, setOpenBranch] = useState(false);
  const [editBranch, setEditBranch] = useState(null);
  const [csvFile, setCsvFile] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState("");

  useEffect(() => {
    api.get("/settings/business").then(({ data }) => setBusiness(data));
    api.get(`/users/${user.id}/avatar`).then(({ data }) => setAvatarPreview(data.avatar || ""));
  }, [user.id]);

  const removeBranch = async (b) => {
    if (!window.confirm(`Delete branch ${b.name}?`)) return;
    try { await api.delete(`/branches/${b.id}`); toast.success("Deleted"); refreshBranches(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const importCsv = async () => {
    if (!csvFile) return;
    const fd = new FormData();
    fd.append("file", csvFile);
    try {
      const token = localStorage.getItem("auth_token");
      const res = await fetch(`${API}/parts/import-csv`, { method: "POST", body: fd, headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Import failed");
      setImportResult(data);
      toast.success(`Imported: ${data.created} created, ${data.updated} updated`);
    } catch (err) { toast.error(err.message); }
  };

  const uploadAvatar = async (file) => {
    if (file.size > 350_000) return toast.error("Please choose an image under ~350 KB");
    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        await api.post("/users/me/avatar", { data_url: reader.result });
        setAvatarPreview(reader.result);
        toast.success("Avatar updated");
      } catch (err) { toast.error(formatApiError(err)); }
    };
    reader.readAsDataURL(file);
  };

  const removeAvatar = async () => {
    await api.delete("/users/me/avatar");
    setAvatarPreview("");
    toast.success("Avatar removed");
  };

  const signOutAll = async () => {
    if (!window.confirm("Sign out from every device where you are logged in? You'll need to sign in again here too.")) return;
    await api.post("/auth/sign-out-all-devices");
    localStorage.removeItem("auth_token");
    window.location.href = "/login";
  };

  return (
    <div className="space-y-8" data-testid="settings-root">
      <div className="border-b border-zinc-200 pb-6">
        <div className="label-eyebrow">Configuration</div>
        <h1 className="text-4xl font-bold tracking-tight text-zinc-950">Settings</h1>
      </div>

      {/* Business */}
      <section className="brutalist-card p-6 space-y-3">
        <div className="flex items-center gap-2"><Building2 className="w-4 h-4" /><h2 className="text-lg font-semibold">Business info (invoice header)</h2></div>
        {business ? (
          <div className="grid grid-cols-2 gap-4 text-sm font-mono">
            <div><div className="label-eyebrow">Name</div>{business.name}</div>
            <div><div className="label-eyebrow">GSTIN</div>{business.gstin || "—"}</div>
            <div className="col-span-2"><div className="label-eyebrow">Address</div>{business.address || "—"}</div>
            <div><div className="label-eyebrow">Phone</div>{business.phone || "—"}</div>
            <div><div className="label-eyebrow">State code</div>{business.state_code || "—"}</div>
          </div>
        ) : <div className="text-sm text-zinc-500">Loading…</div>}
        <p className="text-[11px] font-mono text-zinc-500 pt-2 border-t border-zinc-200">Managed via backend .env (SHOP_NAME, SHOP_GSTIN, SHOP_ADDRESS, SHOP_PHONE, SHOP_STATE_CODE). Contact your admin to change.</p>
      </section>

      {/* Branches */}
      {user.role === "admin" && (
        <section className="brutalist-card">
          <div className="p-6 border-b border-zinc-200 flex items-center justify-between">
            <div className="flex items-center gap-2"><Store className="w-4 h-4" /><h2 className="text-lg font-semibold">Branches / warehouses</h2></div>
            <button data-testid="add-branch-button" onClick={() => { setEditBranch(null); setOpenBranch(true); }} className="bg-zinc-950 text-white text-xs uppercase tracking-wider px-3 py-2 hover:bg-zinc-800 inline-flex items-center gap-1"><Plus className="w-3 h-3" />New branch</button>
          </div>
          <table className="w-full text-sm">
            <thead><tr className="bg-zinc-50 text-zinc-500 uppercase tracking-wider text-[11px] border-b border-zinc-200">
              <th className="text-left px-5 py-3">Code</th><th className="text-left px-5 py-3">Name</th>
              <th className="text-left px-5 py-3">Address</th><th className="text-right px-5 py-3"></th>
            </tr></thead>
            <tbody>
              {branches.map((b) => (
                <tr key={b.id} className="border-t border-zinc-200">
                  <td className="px-5 py-3 font-mono font-medium">{b.code}{b.is_default && <span className="ml-2 text-[10px] uppercase tracking-wider bg-emerald-50 border border-emerald-200 text-emerald-700 px-1.5 py-0.5">default</span>}</td>
                  <td className="px-5 py-3">{b.name}</td>
                  <td className="px-5 py-3 text-xs text-zinc-500">{b.address}</td>
                  <td className="px-5 py-3 text-right space-x-1">
                    <button data-testid={`branch-edit-${b.code}`} onClick={() => { setEditBranch(b); setOpenBranch(true); }} className="p-1.5 border border-zinc-300 hover:bg-zinc-100 inline-flex"><Pencil className="w-3.5 h-3.5" /></button>
                    <button data-testid={`branch-delete-${b.code}`} onClick={() => removeBranch(b)} className="p-1.5 border border-zinc-300 hover:bg-rose-50 hover:border-rose-300 inline-flex"><Trash2 className="w-3.5 h-3.5 text-rose-600" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {/* CSV import */}
      {["admin","inventory_manager"].includes(user.role) && (
        <section className="brutalist-card p-6 space-y-3">
          <div className="flex items-center gap-2"><Upload className="w-4 h-4" /><h2 className="text-lg font-semibold">Bulk import parts (CSV)</h2></div>
          <p className="text-xs text-zinc-500 font-mono">Columns: part_number, name, manufacturer, model, variant, year, purchase_price, mrp, gst_rate, bin_number, reorder_point, reorder_quantity.</p>
          <div className="flex items-center gap-3">
            <input data-testid="import-file-input" type="file" accept=".csv,text/csv" onChange={(e) => setCsvFile(e.target.files?.[0] || null)} className="text-sm" />
            <button data-testid="import-run-button" disabled={!csvFile} onClick={importCsv} className="bg-[#002FA7] text-white text-xs uppercase tracking-wider px-3 py-2 hover:bg-blue-900 disabled:opacity-50">Import</button>
          </div>
          {importResult && (
            <div className="mt-2 font-mono text-xs text-zinc-700 border-t border-zinc-200 pt-3">
              <div>Created: {importResult.created} · Updated: {importResult.updated} · Errors: {importResult.errors?.length || 0}</div>
              {importResult.errors?.length > 0 && <ul className="mt-1 list-disc list-inside text-rose-600">{importResult.errors.slice(0, 10).map((e) => <li key={e}>{e}</li>)}</ul>}
            </div>
          )}
        </section>
      )}

      {/* My phone (self-service) */}
      <MyPhoneSection />

      {/* Avatar + Sign out all */}
      <section className="brutalist-card p-6 space-y-4">
        <div className="flex items-center gap-2"><Camera className="w-4 h-4" /><h2 className="text-lg font-semibold">Your profile</h2></div>
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 bg-zinc-100 border border-zinc-300 flex items-center justify-center overflow-hidden">
            {avatarPreview ? <img src={avatarPreview} alt="avatar" className="w-full h-full object-cover" /> : <span className="font-mono text-2xl text-zinc-400">{user.name?.[0]?.toUpperCase()}</span>}
          </div>
          <div className="space-x-2">
            <label className="cursor-pointer inline-block bg-zinc-950 text-white text-xs uppercase tracking-wider px-3 py-2 hover:bg-zinc-800">
              Upload
              <input data-testid="avatar-upload-input" type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadAvatar(e.target.files[0])} />
            </label>
            {avatarPreview && <button data-testid="avatar-remove-button" onClick={removeAvatar} className="text-xs uppercase tracking-wider px-3 py-2 border border-zinc-300 hover:bg-zinc-50">Remove</button>}
          </div>
        </div>
        <div className="pt-4 border-t border-zinc-200">
          <button data-testid="sign-out-all-button" onClick={signOutAll} className="text-xs uppercase tracking-wider px-4 py-2 border border-rose-300 bg-rose-50 text-rose-700 hover:bg-rose-100 inline-flex items-center gap-2">
            <LogOut className="w-3.5 h-3.5" /> Sign out from all devices
          </button>
          <p className="text-[11px] font-mono text-zinc-500 mt-2">Immediately invalidates every active session for your account across all browsers and devices.</p>
        </div>
      </section>

      {openBranch && <BranchModal initial={editBranch} onClose={() => setOpenBranch(false)} onSaved={() => { setOpenBranch(false); refreshBranches(); }} />}
    </div>
  );
}
